import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import {
  hrApi,
  type Finding,
  type FindingSeverity,
  type OnboardingSummary,
} from "@/services/api/hr";

export const Route = createFileRoute("/portal/hr/onboarding/$id")({
  component: OnboardingDetailPage,
});

const SEVERITY_STYLES: Record<FindingSeverity, string> = {
  error: "bg-red-100 text-red-700",
  warning: "bg-amber-100 text-amber-800",
  pending: "bg-blue-100 text-blue-700",
  pass: "bg-green-100 text-green-700",
};

function FindingRow({ f }: { f: Finding }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <span
        className={`mt-0.5 rounded px-1.5 py-0.5 text-xs font-medium ${
          SEVERITY_STYLES[f.severity] ?? "bg-gray-100 text-gray-700"
        }`}
      >
        {f.severity}
      </span>
      <span>
        {f.field ? <span className="font-medium">{f.field}: </span> : null}
        {f.message}
      </span>
    </li>
  );
}

function PushStatus({ row }: { row: OnboardingSummary }) {
  // Once approved, show the SimplePay push outcome so HR sees per-candidate
  // status + the failure reason — not "see the logs".
  if (row.push_has_error) {
    return (
      <div className="text-sm" data-testid="push-status">
        <p className="font-medium text-red-700">Push to SimplePay failed.</p>
        {row.push_error && <p className="mt-0.5 text-red-600">{row.push_error}</p>}
        <p className="mt-0.5 text-xs text-muted-foreground">
          The system retries automatically. If it keeps failing, the record may
          need correcting.
        </p>
      </div>
    );
  }
  if (row.is_pushed) {
    const when = row.pushed_at_utc
      ? new Date(row.pushed_at_utc).toLocaleDateString()
      : null;
    return (
      <p className="text-sm text-green-700" data-testid="push-status">
        Created in SimplePay
        {row.simplepay_employee_id ? ` (employee ${row.simplepay_employee_id})` : ""}
        {when ? ` on ${when}` : ""}.
      </p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground" data-testid="push-status">
      Approved — queued to push to SimplePay.
    </p>
  );
}

function OnboardingDetailPage() {
  const { id } = useParams({ from: "/portal/hr/onboarding/$id" });
  const onboardingId = Number(id);
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId ?? "";
  const queryClient = useQueryClient();

  // Prefix-invalidate everything under this org's hr-onboarding cache.
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["hr-onboarding", orgId] });

  const enabled = !!token && !!orgId;
  const detail = useQuery({
    queryKey: ["hr-onboarding", orgId, onboardingId],
    queryFn: () => hrApi.get(token, onboardingId),
    enabled,
  });
  const findings = useQuery({
    queryKey: ["hr-onboarding", orgId, onboardingId, "findings"],
    queryFn: () => hrApi.findings(token, onboardingId),
    enabled,
  });
  const documents = useQuery({
    queryKey: ["hr-onboarding", orgId, onboardingId, "documents"],
    queryFn: () => hrApi.documents(token, onboardingId),
    enabled,
  });

  const onErr = (err: unknown) =>
    toast.error(err instanceof Error ? err.message : "Action failed");

  const validate = useMutation({
    mutationFn: () => hrApi.validate(token, onboardingId),
    onSuccess: (run) => {
      toast[run.has_blocking ? "warning" : "success"](
        run.has_blocking ? "Validation found blocking errors" : "Validation passed",
      );
      queryClient.invalidateQueries({
        queryKey: ["hr-onboarding", orgId, onboardingId, "findings"],
      });
    },
    onError: onErr,
  });
  const invite = useMutation({
    mutationFn: () => hrApi.invite(token, onboardingId, "email"),
    onSuccess: (r) => {
      // Delivery is best-effort; the capture link is always returned so HR can
      // share it manually even when email delivery didn't succeed.
      if (r.delivery_status === "queued") {
        toast.success("Capture link emailed to the candidate.");
      } else {
        toast.warning(
          `Email delivery: ${r.delivery_status}. Share this link manually: ${r.capture_url}`,
          { duration: 15000 },
        );
      }
      invalidate();
    },
    onError: onErr,
  });
  const managerApprove = useMutation({
    mutationFn: () => hrApi.managerApprove(token, onboardingId),
    onSuccess: () => {
      toast.success("Line-manager approval recorded");
      invalidate();
    },
    onError: onErr,
  });
  const hrApprove = useMutation({
    mutationFn: () => hrApi.hrApprove(token, onboardingId),
    onSuccess: () => {
      toast.success("Approved — employee ready to push to payroll");
      invalidate();
    },
    onError: onErr,
  });
  const reject = useMutation({
    mutationFn: (reason: string) => hrApi.reject(token, onboardingId, reason || undefined),
    onSuccess: () => {
      toast.success("Onboarding rejected");
      invalidate();
    },
    onError: onErr,
  });

  const download = async (docId: number) => {
    // Open the tab synchronously (before the await) so popup blockers don't
    // eat it, then redirect it to the presigned URL.
    const tab = window.open("", "_blank", "noopener");
    try {
      const dl = await hrApi.documentDownload(token, onboardingId, docId);
      if (tab) tab.location.href = dl.url;
      else window.open(dl.url, "_blank", "noopener");
    } catch (err) {
      tab?.close();
      onErr(err);
    }
  };

  if (detail.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (detail.isError || !detail.data)
    return <p className="text-sm text-red-600">Onboarding session not found.</p>;

  const row = detail.data;
  const status = row.status;
  const name = [row.first_name, row.last_name].filter(Boolean).join(" ").trim() || "—";
  const busy =
    managerApprove.isPending ||
    hrApprove.isPending ||
    reject.isPending ||
    validate.isPending ||
    invite.isPending;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/portal/hr/onboarding" className="text-sm text-muted-foreground hover:underline">
          ← Back to onboarding
        </Link>
        <h1 className="mt-1 text-2xl font-semibold">{name}</h1>
        <p className="text-sm text-muted-foreground">
          {row.employee_type.replace(/_/g, " ")} · status: {status.replace(/_/g, " ")}
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Validation</CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={() => validate.mutate()}
            disabled={busy}
            data-testid="run-validation"
          >
            {validate.isPending ? "Validating…" : "Re-run validation"}
          </Button>
        </CardHeader>
        <CardContent>
          {findings.isError ? (
            <p className="text-sm text-red-600">Failed to load findings.</p>
          ) : findings.data && findings.data.length > 0 ? (
            <ul className="space-y-1.5">
              {findings.data.map((f, i) => (
                <FindingRow key={`${f.rule_key}-${i}`} f={f} />
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              No findings yet — run validation to check the record.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Documents</CardTitle>
        </CardHeader>
        <CardContent>
          {documents.isError ? (
            <p className="text-sm text-red-600">Failed to load documents.</p>
          ) : documents.data && documents.data.length > 0 ? (
            <ul className="space-y-1.5">
              {documents.data.map((d) => (
                <li key={d.id} className="flex items-center justify-between text-sm">
                  <span>
                    <span className="font-medium">{d.document_type.replace(/_/g, " ")}</span>{" "}
                    — {d.filename ?? "file"}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => download(d.id)}>
                    Download
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Approval</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {(status === "draft" || status === "invited") && (
            <Button variant="outline" onClick={() => invite.mutate()} disabled={invite.isPending}>
              {invite.isPending ? "Sending…" : "Send capture link"}
            </Button>
          )}
          {status === "captured" && (
            <Button
              onClick={() => managerApprove.mutate()}
              disabled={busy}
              data-testid="approve-manager"
            >
              Line-manager approve
            </Button>
          )}
          {status === "manager_approved" && (
            <Button onClick={() => hrApprove.mutate()} disabled={busy} data-testid="approve-hr">
              HR approve
            </Button>
          )}
          {status !== "approved" && status !== "rejected" && (
            <RejectDialog
              disabled={busy}
              pending={reject.isPending}
              onReject={(reason) => reject.mutate(reason)}
            />
          )}
          {status === "approved" && <PushStatus row={row} />}
          {status === "rejected" && <p className="text-sm text-red-700">This onboarding was rejected.</p>}
        </CardContent>
      </Card>
    </div>
  );
}

function RejectDialog({
  disabled,
  pending,
  onReject,
}: {
  disabled: boolean;
  pending: boolean;
  onReject: (reason: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive" disabled={disabled} data-testid="reject">
          Reject
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject onboarding?</DialogTitle>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="reject-reason">Reason (optional)</Label>
          <Input
            id="reject-reason"
            value={reason}
            maxLength={1000}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. banking proof missing"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={pending}
            data-testid="reject-confirm"
            onClick={() => {
              onReject(reason);
              setOpen(false);
            }}
          >
            {pending ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
